import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Slider,
    TextField,
    Typography,
    Alert,
} from "@mui/material";

import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

import { getAuth, reauthenticateWithCredential, updatePassword, EmailAuthProvider } from "firebase/auth";
import { db, storage } from "../../firebase";
import { collection, getDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

function UserEdit() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [user, setUser] = useState("");
    const [param, setParam] = useState("");

    const [imgUrl, setImgUrl] = useState("");
    const [imgUrlOverrap, setImgUrlOverrap] = useState("");
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [frequency, setFrequency] = useState();
    const [twitter, setTwitter] = useState("");

    const [avaterOpen, setAvaterOpen] = useState(false);
    const fileInputRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [croppedImgSrc, setCroppedImgSrc] = useState("");
    const [cropper, setCropper] = useState(null);

    const handleAvaterOpen = () => {
        fileInputRef.current.value = "";
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = event.target.files;
        if (files) {
            setAvaterOpen(true);
            const reader = new FileReader();
            reader.onload = () => {
                setImgSrc(reader.result);
            };
            reader.readAsDataURL(files[0]);
        }
    };

    const onCropperInit = (instance) => {
        setCropper(instance);
    };

    // トリミングする
    const handleCrop = () => {
        if (cropper) {
            const canvas = cropper.getCroppedCanvas();
            const dataUrl = canvas.toDataURL();
            setImgUrl(dataUrl);
            setCroppedImgSrc(canvas);
        }
        handleCancel();
    };

    // トリミングをキャンセルする
    const handleCancel = () => {
        setAvaterOpen(false);
        setImgSrc(null);
    };

    const handleNameChange = (event) => {
        setName(event.target.value);
    };

    const handleDescChange = (event) => {
        setDesc(event.target.value);
    };

    const handleFreqChange = (event, value) => {
        setFrequency(value);
    };

    const handleTwitterChange = (event) => {
        setTwitter(event.target.value);
    };

    const handleUpdateClick = async () => {
        if (imgUrl === imgUrlOverrap) {
            saveTofirestoreSameImg();
        } else if (imgUrl) {
            const strageRef = ref(storage, `member/${user}`);

            const blob = await new Promise((resolve) => {
                croppedImgSrc.toBlob((blob) => resolve(blob));
            });

            try {
                await uploadBytes(strageRef, blob);
                const url = await getDownloadURL(strageRef);
                saveToFirestore(url);
            } catch (error) {
                console.error("Error uploading img: ", error);
            }
        } else {
            saveToFirestoreNotImg();
        }
    };

    const saveTofirestoreSameImg = async () => {
        try {
            const documentRef = doc(db, "member", user);
            await updateDoc(documentRef, {
                name: name,
                desc: desc,
                frequency: frequency,
                twitter: twitter,
                updateAt: serverTimestamp(),
            });
        } catch (error) {
            console.log("error: ", error);
        }

        setSaveOpen(true);
    };

    const saveToFirestore = async (url) => {
        try {
            const documentRef = doc(db, "member", user);
            await updateDoc(documentRef, {
                img: url,
                name: name,
                desc: desc,
                frequency: frequency,
                twitter: twitter,
                updateAt: serverTimestamp(),
            });
        } catch (error) {
            console.error("error: ", error);
        }

        setSaveOpen(true);
    };

    const saveToFirestoreNotImg = async () => {
        try {
            const documentRef = doc(db, "member", user);
            await updateDoc(documentRef, {
                img: "",
                name: name,
                desc: desc,
                frequency: frequency,
                twitter: twitter,
                updateAt: serverTimestamp(),
            });
        } catch (error) {
            console.error("error: ", error);
        }

        setSaveOpen(true);
    };

    const [saveOpen, setSaveOpen] = useState(false);
    const handleSaved = () => {
        window.scrollTo(0, 0);
        navigate("/", { replace: true });
    };

    const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);
    const handlePasswordChangeOpen = () => {
        setPasswordChangeOpen(true);
    };

    const handlePasswordChangeClose = () => {
        setPasswordChangeOpen(false);
    };

    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleOldPasswordChange = (event) => {
        setOldPassword(event.target.value);
    };

    const handleNewPasswordChange = (event) => {
        setNewPassword(event.target.value);
    };

    const handleNewPasswordConfirmChange = (event) => {
        setNewPasswordConfirm(event.target.value);
    };

    const handlePasswordChange = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
        const credential = EmailAuthProvider.credential(user.email, oldPassword);

        try {
            await reauthenticateWithCredential(user, credential);

            if (newPassword !== newPasswordConfirm) {
                setErrorMessage("新しいパスワードが一致しません");
                return;
            }

            if (newPassword.length < 8) {
                setErrorMessage("パスワードは8文字以上で設定してください");
                return;
            }

            if (!/[a-z]/.test(newPassword) || !/[A-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
                setErrorMessage("パスワードには大文字、小文字、数字を含む8文字以上である必要があります");
                return;
            }

            await updatePassword(user, newPassword);
            setPasswordChangeOpen(false);
            setOldPassword("");
            setNewPassword("");
            setNewPasswordConfirm("");
            setErrorMessage("");
            setSaveOpen(true);
        } catch (error) {
            setErrorMessage("現在のパスワードが正しくありません");
        }
    };

    const fetchUserData = async (uid) => {
        const params = window.location.pathname.split("/")[1];
        setParam(params);

        const memberRef = collection(db, "member");
        const memberDoc = doc(memberRef, uid);
        const memberSnapshot = await getDoc(memberDoc);

        if (memberSnapshot.exists()) {
            setImgUrl(memberSnapshot.data().img);
            setImgUrlOverrap(memberSnapshot.data().img);
            setName(memberSnapshot.data().name);
            setDesc(memberSnapshot.data().desc);
            setFrequency(memberSnapshot.data().frequency);
            setTwitter(memberSnapshot.data().twitter);
        }

        setLoading(false);
    };

    useEffect(() => {
        const unsubscribe = getAuth().onAuthStateChanged(async (userLogin) => {
            if (userLogin) {
                const uid = userLogin.uid;
                setUser(uid);
                await fetchUserData(uid);
            } else {
                window.scrollTo(0, 0);
                navigate("/login", { replace: true });
            }
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <Box
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Helmet>
                <title>User Edit - SOG Member Site</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Container maxWidth="1280" sx={{ display: { xs: { maxWidth: 0 } }, maxWidth: 1280 }}>
                {user === param ? (
                    <Container>
                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mt: 10 }}
                        >
                            <Typography variant="h6">ユーザー情報編集</Typography>
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ m: 3 }}
                        >
                            <Box
                                sx={{
                                    minWidth: 350,
                                    minHeight: 350,
                                    border: "1px solid",
                                    borderColor: "primary.main",
                                    borderRadius: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    objectFit: "contain",
                                }}
                                onClick={handleAvaterOpen}
                            >
                                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} ref={fileInputRef} />

                                {imgUrl ? (
                                    <img
                                        src={imgUrl}
                                        alt="uploaded"
                                        style={{
                                            maxWidth: 348,
                                            maxHeight: 348,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            color: "text.secondary",
                                        }}
                                        type="file"
                                        onChange={handleAvaterOpen}
                                    >
                                        画像をアップロード
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ m: 3 }}
                        >
                            <TextField variant="filled" label="名前" value={name} onChange={handleNameChange} sx={{ minWidth: 350 }} />
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ m: 3 }}
                        >
                            <TextField
                                variant="filled"
                                label="プロフィール"
                                value={desc}
                                onChange={handleDescChange}
                                multiline
                                rows={5}
                                sx={{ minWidth: 350 }}
                            />
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mt: 3 }}
                        >
                            <Grid container sx={{ maxWidth: 350 }}>
                                <Grid item xs={6} sm={6} style={{ justifyContent: "left" }}>
                                    <Typography>PL</Typography>
                                </Grid>
                                <Grid item xs={6} sm={6}>
                                    <Typography style={{ textAlign: "right" }}>KP</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mb: 3 }}
                        >
                            <Slider value={frequency} onChange={handleFreqChange} min={0} max={10} step={1} sx={{ maxWidth: 330 }} />
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ m: 3 }}
                        >
                            <TextField variant="filled" label="Twitter URL" value={twitter} onChange={handleTwitterChange} sx={{ minWidth: 350 }} />
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mt: 5 }}
                        >
                            <Button variant="contained" onClick={handleUpdateClick} size="large" sx={{ width: "350px" }}>
                                更新
                            </Button>
                        </Box>

                        <Box
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                            sx={{ mt: 5, mb: 2 }}
                        >
                            <Button variant="contained" color="info" onClick={handlePasswordChangeOpen} size="large" sx={{ width: "350px" }}>
                                パスワード変更
                            </Button>
                        </Box>
                    </Container>
                ) : (
                    <div>
                        <Typography
                            sx={{ mt: 10, mb: 2 }}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            404 Not Found
                        </Typography>
                    </div>
                )}
            </Container>

            <Dialog open={saveOpen}>
                <DialogTitle
                    sx={{
                        minWidth: 263,
                        justifyContent: "center",
                        display: "flex",
                    }}
                >
                    更新しました
                </DialogTitle>
                <DialogActions sx={{ justifyContent: "center", display: "flex", mb: 2 }}>
                    <Button variant="contained" sx={{ width: "100px" }} onClick={handleSaved}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={avaterOpen} onClose={handleCancel}>
                <DialogTitle
                    sx={{
                        minWidth: 330,
                        justifyContent: "center",
                        display: "flex",
                    }}
                >
                    トリミング
                </DialogTitle>
                <DialogContent>
                    {imgSrc && (
                        <Box>
                            <Cropper
                                src={imgSrc}
                                style={{ height: 350, width: "100%" }}
                                initialAspectRatio={1}
                                cropBoxMovable={false}
                                cropBoxResizable={false}
                                guides={false}
                                dragMode="move"
                                onInitialized={(instance) => onCropperInit(instance)}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCancel}>
                        キャンセル
                    </Button>
                    <Button onClick={handleCrop} variant="contained">
                        切り抜く
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog fullWidth maxWidth="xs" open={passwordChangeOpen} onClose={handlePasswordChangeClose}>
                <DialogTitle>パスワード変更</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            fullWidth
                            type="password"
                            variant="filled"
                            label="現在のパスワード"
                            value={oldPassword}
                            onChange={handleOldPasswordChange}
                            sx={{ mb: 5 }}
                        />
                        <TextField
                            fullWidth
                            type="password"
                            variant="filled"
                            label="新しいパスワード"
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                        />
                        <TextField
                            fullWidth
                            type="password"
                            variant="filled"
                            label="新しいパスワード確認"
                            value={newPasswordConfirm}
                            onChange={handleNewPasswordConfirmChange}
                        />
                        <Alert severity="info">英数字8文字以上で設定</Alert>

                        {errorMessage && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {errorMessage}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handlePasswordChangeClose}>
                        キャンセル
                    </Button>
                    <Button variant="contained" onClick={handlePasswordChange}>
                        変更
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default UserEdit;
